import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { GripVertical, Save } from "lucide-react";
import { ACTIONS } from "@/components/CommandPalette";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import api from "@/api/client";

// Sortable Item Component
function SortableMenuItem({ item, index, onToggle, currentUserRole }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Finde die Action mit Icon und Farbe
  const action = ACTIONS.find(a => a.id === item.id);
  const Icon = action?.icon;
  const iconColor = action?.color || "text-neutral-600";

  // Prüfe Rollen-Berechtigung - zeige Item nicht wenn User keine Berechtigung hat
  if (action?.roles && currentUserRole && !action.roles.includes(currentUserRole)) {
    return null; // Item nicht anzeigen
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 px-3 py-2 border rounded bg-white hover:bg-neutral-50 transition-colors"
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing touch-none"
      >
        <GripVertical className="w-4 h-4 text-neutral-400" />
      </div>

      {/* Checkbox */}
      <Checkbox
        checked={item.enabled}
        onCheckedChange={() => onToggle(item.id)}
        id={`checkbox-${item.id}`}
      />

      {/* Icon */}
      {Icon && <Icon className={`h-4 w-4 ${iconColor}`} />}

      {/* Label */}
      <label
        htmlFor={`checkbox-${item.id}`}
        className="flex-1 text-sm cursor-pointer"
      >
        {item.label}
      </label>

      {/* Order Badge */}
      <span className="text-xs text-neutral-400 font-mono">
        #{index + 1}
      </span>
    </div>
  );
}

export default function MenuConfigDialog({ open, onClose, currentUser }) {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(currentUser?.id);
  const [config, setConfig] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const isSuperadmin = currentUser?.role === "superadmin";

  // Setze selectedUserId beim Öffnen des Dialogs
  useEffect(() => {
    if (open && currentUser?.id) {
      setSelectedUserId(currentUser.id);
    }
  }, [open, currentUser]);

  // Lade alle Benutzer (nur für Superadmin)
  useEffect(() => {
    if (open && isSuperadmin) {
      api.get("/users")
        .then((res) => {
          // Nur Benutzer mit Dashboard-Zugang (superadmin, backoffice)
          const dashboardUsers = res.data.filter(u => 
            u.role === "superadmin" || u.role === "backoffice"
          );
          setUsers(dashboardUsers);
        })
        .catch(err => console.error("Fehler beim Laden der Benutzer:", err));
    }
  }, [open, isSuperadmin]);

  // Lade Konfiguration für ausgewählten Benutzer
  useEffect(() => {
    if (open && selectedUserId) {
      setLoading(true);
      api.get(`/users/${selectedUserId}/command-palette-config`)
        .then((res) => {
          let userConfig = res.data.config || [];
          
          // Füge neue Items aus ACTIONS hinzu, die noch nicht in der Config sind
          const existingIds = new Set(userConfig.map(item => item.id));
          const newItems = ACTIONS.filter(action => !existingIds.has(action.id))
            .map((action, idx) => ({
              id: action.id,
              label: action.label,
              enabled: true,
              order: userConfig.length + idx
            }));
          
          // Kombiniere bestehende Config mit neuen Items
          const mergedConfig = [...userConfig, ...newItems];
          
          setConfig(mergedConfig);
        })
        .catch(err => console.error("Fehler beim Laden der Konfiguration:", err))
        .finally(() => setLoading(false));
    }
  }, [open, selectedUserId]);

  // Drag & Drop Sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleToggle = (id) => {
    setConfig(config.map(item => 
      item.id === id ? { ...item, enabled: !item.enabled } : item
    ));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setConfig((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newConfig = arrayMove(items, oldIndex, newIndex);
        
        // Update order
        newConfig.forEach((item, idx) => item.order = idx);
        
        return newConfig;
      });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/users/${selectedUserId}/command-palette-config`, config);
      if (selectedUserId === currentUser?.id) {
        window.dispatchEvent(new Event("menu-config-updated"));
      }
      alert("Einstellungen gespeichert!");
      onClose();
    } catch (err) {
      alert("Fehler beim Speichern: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isSuperadmin ? "Menü-Verwaltung" : "Meine Menü-Einstellungen"}
          </DialogTitle>
        </DialogHeader>

        {/* User Selection (nur für Superadmin) */}
        {isSuperadmin && (
          <div className="mb-6">
            <label className="text-sm font-medium mb-2 block">Benutzer auswählen</label>
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger>
                <SelectValue placeholder="Benutzer wählen..." />
              </SelectTrigger>
              <SelectContent>
                {users.map(user => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.email} ({user.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Info Text */}
        <p className="text-sm text-neutral-600 mb-4">
          Wählen Sie, welche Menüpunkte im "Aktionen"-Menü angezeigt werden sollen und legen Sie die Reihenfolge fest.
        </p>

        {/* Config List mit Drag & Drop */}
        {loading ? (
          <div className="text-center py-8 text-neutral-500">Lade Konfiguration...</div>
        ) : (
          <div className="mb-6">
            <p className="text-xs text-neutral-500 mb-3 flex items-center gap-2">
              <GripVertical className="w-3 h-3" />
              Ziehen Sie die Einträge, um die Reihenfolge zu ändern
            </p>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={config.map(item => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-1.5">
                  {config
                    .filter(item => {
                      // Filtere Items nach Rollen-Berechtigung
                      const action = ACTIONS.find(a => a.id === item.id);
                      if (!action?.roles || !currentUser?.role) return true;
                      return action.roles.includes(currentUser.role);
                    })
                    .map((item, index) => (
                      <SortableMenuItem
                        key={item.id}
                        item={item}
                        index={index}
                        onToggle={handleToggle}
                        currentUserRole={currentUser?.role}
                      />
                    ))
                  }
                </div>
              </SortableContext>
            </DndContext>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Speichere..." : "Speichern"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
